import React, { useState } from 'react';
import { UserGroup } from 'src/shared/types';
import StandardAppLayout from 'src/shared/components/layout/standard-app-layout/StandardAppLayout';
import useAuthService from 'src/shared/hooks/useAuthService';
import AccessDenied from 'src/shared/components/access-denied/AccessDenied';
import { PrimaryButton, SecondaryButton } from 'src/shared/components/button/Button';
import config from 'src/config';
import authService from 'src/services/auth-service';
import authStorageService from 'src/shared/hooks/services/auth-storage-service';
import Input from 'src/shared/components/input/Input';

const ImpersonateUser = () => {
    const { currentUser, signout } = useAuthService();
    const [email, setEmail] = useState('');
    const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);
    const hasImpersonatePermission = currentUser?.groups?.includes(UserGroup.IMPERSONATOR);

    if (!hasImpersonatePermission) {
        return <AccessDenied />;
    }
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setSubmittedEmail(email);
        impersonateUser(email);
    };

    const impersonateUser = async (impersonateEmail: string) => {
        try {
            const token = await authService.getAccessToken();
            const response = await fetch(`${config.apiBaseUrl}/impersonate`, {
                method: 'POST',
                headers: {
                    Authorization: token ?? '',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ impersonate_email: impersonateEmail }),
            });

            if (!response.ok) {
                throw new Error('Impersonation failed');
            }

            const data = await response.json();
            authStorageService.clearAll();
            authStorageService.setAccessToken(data.accessToken);
            authStorageService.setAccessTokenExpiry(data.accessTokenExpiresAt);
            authStorageService.setRefreshToken(data.refreshToken);
            authStorageService.setRefreshTokenExpiry(data.refreshTokenExpiresAt);
            window.location.assign('/customers');
        } catch (error) {
            alert('Impersonation failed: ' + error);
        }
    };

    const mainContent = (
        <div>
            <p>Use this feature to test user permissions and functionality as if you were another user.</p>
            <p>Please ensure you have the necessary permissions to impersonate users.</p>
            <form className='mt-6 flex flex-col gap-4 max-w-md' onSubmit={handleSubmit}>
                <label className='font-medium'>
                    User Email
                    <Input
                        type='email'
                        className='mt-1 block w-full rounded border border-gray-300 px-3 py-2 focus:outline-none focus:ring focus:border-blue-400'
                        placeholder='user@example.com'
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        required
                    />
                </label>
                <PrimaryButton type='submit' className='w-fit bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition'>
                    Impersonate
                </PrimaryButton>
            </form>
            {submittedEmail && (
                <div className='mt-4  flex flex-col gap-2'>
                    <div>
                        Attempting to impersonate: <span className='font-semibold'>{submittedEmail}</span>
                    </div>
                    <SecondaryButton type='button' className='w-fit px-4 py-2 rounded  transition' onClick={signout}>
                        Sign Out
                    </SecondaryButton>
                </div>
            )}
        </div>
    );
    return (
        <StandardAppLayout
            title='Impersonate User'
            subTitle='This page allows you to impersonate another user for testing purposes.'
            breadcrumbs={[{ label: 'Home', path: '/' }]}
            mainContent={mainContent}
        />
    );
};

export default ImpersonateUser;
